// @ts-nocheck
// ═══ Post-Processing Effects — Audio-reactive visual effects layer ═══
//
// Each effect renders as an overlay layer. Effects can be:
// - "auto": intensity driven by audio frequency bands
// - "manual": fixed intensity (0-1)
//
// Usage in Composition:
//   <PostProcessingStack effects={project.effects} />

import React from 'react'
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { useAudioReactivity } from './AudioReactivity'
import { useBeatContext } from './BeatDetection'
import type { EffectLayer } from './types'

// ═══ Effect Stack — renders all enabled effects as overlay layers ═══

export const PostProcessingStack: React.FC<{
  effects: EffectLayer[]
  sectionIntensity?: number // 0-1 from current section
}> = ({ effects, sectionIntensity = 0.5 }) => {
  return (
    <>
      {effects.filter(e => e.enabled).map(effect => (
        <EffectRenderer key={effect.id} effect={effect} sectionIntensity={sectionIntensity} />
      ))}
    </>
  )
}

// ═══ Effect Router ═══

const EffectRenderer: React.FC<{
  effect: EffectLayer
  sectionIntensity: number
}> = ({ effect, sectionIntensity }) => {
  switch (effect.type) {
    case 'bloom': return <BloomEffect effect={effect} sectionIntensity={sectionIntensity} />
    case 'glitch': return <GlitchEffect effect={effect} sectionIntensity={sectionIntensity} />
    case 'chromatic-aberration': return <ChromaticAberrationEffect effect={effect} sectionIntensity={sectionIntensity} />
    case 'grain': return <GrainEffect effect={effect} />
    case 'vignette': return <VignetteEffect effect={effect} />
    default: return null
  }
}

// ═══ Helper: resolve intensity (auto vs manual) ═══

function useEffectIntensity(
  effect: EffectLayer,
  sectionIntensity: number,
  audioValue: number, // the audio metric to drive auto mode
): number {
  const mode = (effect.settings?.mode as string) || 'auto'
  const baseOpacity = effect.opacity

  if (mode === 'manual') {
    const manualIntensity = (effect.settings?.intensity as number) ?? 0.5
    return baseOpacity * manualIntensity * sectionIntensity
  }

  // Auto mode: audio-driven
  return baseOpacity * audioValue * sectionIntensity
}

// ═══ 1. Bloom Effect ═══
// Bright glow that intensifies on bass/volume

const BloomEffect: React.FC<{
  effect: EffectLayer
  sectionIntensity: number
}> = ({ effect, sectionIntensity }) => {
  const { bass, volume } = useAudioReactivity()
  const intensity = useEffectIntensity(effect, sectionIntensity, Math.max(bass, volume))
  const color = (effect.settings?.color as string) || '#ffffff'
  const radius = interpolate(intensity, [0, 1], [0, 40], { extrapolateRight: 'clamp' })

  if (intensity < 0.01) return null

  return (
    <AbsoluteFill style={{
      background: `radial-gradient(ellipse at center, ${color}${Math.round(intensity * 30).toString(16).padStart(2, '0')} 0%, transparent 70%)`,
      filter: `blur(${radius}px)`,
      mixBlendMode: 'screen',
      pointerEvents: 'none',
    }} />
  )
}

// ═══ 2. Glitch Effect ═══
// RGB split + displacement on beats

const GlitchEffect: React.FC<{
  effect: EffectLayer
  sectionIntensity: number
}> = ({ effect, sectionIntensity }) => {
  const frame = useCurrentFrame()
  const { beatIntensity, isOnBeat } = useBeatContext()
  const { bass } = useAudioReactivity()
  const intensity = useEffectIntensity(effect, sectionIntensity, beatIntensity)

  if (intensity < 0.05) return null

  // Pseudo-random from frame (deterministic for rendering)
  const seed = Math.sin(frame * 12.9898) * 43758.5453
  const rand = seed - Math.floor(seed)
  const offsetX = (rand - 0.5) * intensity * 30
  const offsetY = (Math.sin(frame * 0.7) - 0.5) * intensity * 10

  // Scan line position
  const scanY = ((frame * 3) % 100)

  return (
    <>
      {/* RGB split layers */}
      <AbsoluteFill style={{
        backgroundColor: 'red',
        opacity: intensity * 0.08,
        mixBlendMode: 'screen',
        transform: `translate(${offsetX}px, ${offsetY * 0.5}px)`,
        pointerEvents: 'none',
      }} />
      <AbsoluteFill style={{
        backgroundColor: 'cyan',
        opacity: intensity * 0.06,
        mixBlendMode: 'screen',
        transform: `translate(${-offsetX * 0.7}px, ${-offsetY * 0.3}px)`,
        pointerEvents: 'none',
      }} />

      {/* Scan lines */}
      <AbsoluteFill style={{
        background: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0,0,0,${intensity * 0.15}) 2px,
          rgba(0,0,0,${intensity * 0.15}) 4px
        )`,
        pointerEvents: 'none',
      }} />

      {/* Displacement bar */}
      {intensity > 0.3 && (
        <div style={{
          position: 'absolute',
          top: `${scanY}%`,
          left: 0,
          right: 0,
          height: `${2 + intensity * 8}px`,
          backgroundColor: `rgba(255,255,255,${intensity * 0.1})`,
          transform: `translateX(${offsetX * 2}px)`,
          pointerEvents: 'none',
        }} />
      )}
    </>
  )
}

// ═══ 3. Chromatic Aberration ═══
// RGB channel offset that increases with mid frequencies

const ChromaticAberrationEffect: React.FC<{
  effect: EffectLayer
  sectionIntensity: number
}> = ({ effect, sectionIntensity }) => {
  const { mid, high } = useAudioReactivity()
  const intensity = useEffectIntensity(effect, sectionIntensity, Math.max(mid, high * 0.7))

  if (intensity < 0.02) return null

  const offset = interpolate(intensity, [0, 1], [0, 6], { extrapolateRight: 'clamp' })

  return (
    <>
      <AbsoluteFill style={{
        backgroundColor: 'red',
        opacity: intensity * 0.04,
        mixBlendMode: 'screen',
        transform: `translate(${offset}px, 0)`,
        pointerEvents: 'none',
      }} />
      <AbsoluteFill style={{
        backgroundColor: 'blue',
        opacity: intensity * 0.04,
        mixBlendMode: 'screen',
        transform: `translate(${-offset}px, 0)`,
        pointerEvents: 'none',
      }} />
    </>
  )
}

// ═══ 4. Film Grain ═══
// Animated noise overlay — purely visual texture

const GrainEffect: React.FC<{
  effect: EffectLayer
}> = ({ effect }) => {
  const frame = useCurrentFrame()
  const opacity = effect.opacity
  const grainSize = (effect.settings?.size as number) || 1

  if (opacity < 0.01) return null

  // SVG noise filter with animated seed
  const seed = frame % 100

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', opacity }}>
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <filter id={`grain-${effect.id}`}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency={0.65 / grainSize}
              numOctaves={3}
              seed={seed}
              stitchTiles="stitch"
            />
            <feColorMatrix type="saturate" values="0" />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter={`url(#grain-${effect.id})`} opacity={0.3} />
      </svg>
    </AbsoluteFill>
  )
}

// ═══ 5. Vignette ═══
// Darkened edges — classic film look

const VignetteEffect: React.FC<{
  effect: EffectLayer
}> = ({ effect }) => {
  const opacity = effect.opacity
  const spread = (effect.settings?.spread as number) || 0.6
  const color = (effect.settings?.color as string) || '#000000'

  if (opacity < 0.01) return null

  return (
    <AbsoluteFill style={{
      background: `radial-gradient(ellipse at center, transparent ${spread * 100}%, ${color} 100%)`,
      opacity,
      pointerEvents: 'none',
    }} />
  )
}
